import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, MessageCircle, MapPin, FileText, Hammer, ClipboardCheck, CheckCircle } from 'lucide-react';

const processSteps = [
  {
    number: '01',
    title: 'Inquiry & Phone Consult',
    description: 'Your journey begins with a simple phone call or form submission. We\'ll discuss your vision, timeline, and budget to ensure we\'re the right fit for your project.',
    icon: MessageCircle,
    details: [
      'Quick response within 24 hours',
      'Initial project discussion',
      'Budget range alignment',
      'Timeline expectations',
    ],
  },
  {
    number: '02',
    title: 'Site Visit & Scope Alignment',
    description: 'We visit your home to understand the space, take measurements, discuss design preferences, and align on the full scope of work.',
    icon: MapPin,
    details: [
      'In-person walkthrough',
      'Detailed measurements',
      'Design preferences discussion',
      'Material considerations',
    ],
  },
  {
    number: '03',
    title: 'Detailed Estimate & Schedule',
    description: 'You receive a comprehensive, itemized estimate with clear pricing and a realistic project schedule. No surprises, no hidden fees.',
    icon: FileText,
    details: [
      'Itemized cost breakdown',
      'Material specifications',
      'Project timeline',
      'Payment schedule',
    ],
  },
  {
    number: '04',
    title: 'Pre-Start Walkthrough',
    description: 'Before work begins, we conduct a final walkthrough to confirm all details, review material selections, and establish our home protection plan.',
    icon: ClipboardCheck,
    details: [
      'Material selection confirmation',
      'Protection plan for your home',
      'Work area preparation',
      'Communication preferences',
    ],
  },
  {
    number: '05',
    title: 'Build Phase',
    description: 'Our skilled craftsmen execute your project with precision and care. You\'ll receive regular updates and we maintain a clean, protected workspace throughout.',
    icon: Hammer,
    details: [
      'Daily/weekly progress updates',
      'Dust barriers & floor protection',
      'Daily cleanup protocols',
      'Quality checkpoints',
    ],
  },
  {
    number: '06',
    title: 'Final Walkthrough & Closeout',
    description: 'We conduct a thorough final walkthrough together, address any punch list items, and ensure your complete satisfaction before project completion.',
    icon: CheckCircle,
    details: [
      'Detailed inspection together',
      'Punch list completion',
      'Final cleanup',
      'Care & maintenance guidance',
    ],
  },
];

export const Process = () => {
  return (
    <main data-testid="process-page">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="w-16 h-px bg-[#C6A87C] mb-8" />
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-6">Our Process</h1>
            <p className="text-gray-600 text-lg leading-relaxed">
              A refined, transparent approach designed to deliver exceptional results with minimal stress. From initial consultation to final walkthrough, we guide you every step of the way.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="space-y-16 md:space-y-24">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
                  {/* Number & Icon */}
                  <div className="md:col-span-3 flex md:flex-col items-center md:items-start gap-4">
                    <span className="font-heading text-6xl md:text-7xl text-[#C6A87C]/20">
                      {step.number}
                    </span>
                    <div className="w-14 h-14 flex items-center justify-center border border-[#C6A87C]/30">
                      <step.icon className="w-6 h-6 text-[#C6A87C]" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="md:col-span-9">
                    <h2 className="font-heading text-2xl md:text-3xl mb-4">{step.title}</h2>
                    <p className="text-gray-600 leading-relaxed mb-6">{step.description}</p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {step.details.map((detail) => (
                        <li key={detail} className="flex items-center gap-3 text-sm text-gray-700">
                          <span className="w-1.5 h-1.5 bg-[#C6A87C] rounded-full flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Connector Line */}
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute left-[5.5rem] top-32 bottom-0 w-px bg-[#E5E5E5] -mb-24" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Jax Difference */}
      <section className="py-24 md:py-32 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="w-16 h-px bg-[#C6A87C] mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl mb-4">The Jax Difference</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              What sets us apart throughout every project phase.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white p-8"
            >
              <h3 className="font-heading text-xl mb-4">Transparent Communication</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Daily or weekly updates via your preferred method. You'll never wonder about the status of your project. We're always accessible and responsive to your questions.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white p-8"
            >
              <h3 className="font-heading text-xl mb-4">Clean Worksite Standards</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Dust barriers, floor protection, and daily cleanup are standard. We treat your home with the same respect we'd treat our own. Your living space remains as comfortable as possible.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white p-8"
            >
              <h3 className="font-heading text-xl mb-4">No-Surprise Process</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Clear scope documentation, honest timelines, and transparent change-order processes. If something unexpected arises, you'll know about it immediately with clear options.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-px bg-[#C6A87C] mx-auto mb-8" />
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
              Ready to Begin?
            </h2>
            <p className="text-gray-400 text-lg mb-10">
              Start with a simple conversation. We're here to answer your questions and discuss your project.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="tel:9048636673"
                data-testid="process-call-btn"
                className="inline-flex items-center justify-center gap-3 bg-[#C6A87C] text-white px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
              >
                <Phone className="w-4 h-4" />
                (904) 863-6673
              </a>
              <Link
                to="/contact"
                data-testid="process-estimate-btn"
                className="inline-flex items-center justify-center gap-3 bg-white text-[#0A0A0A] px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-gray-100 transition-colors"
              >
                Request Consultation
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};
